import { Controller } from "@hotwired/stimulus"

/**
 * Tap-to-define for OpenStax key terms.
 *
 * OpenStax marks every term's first use as `<span data-type="term">`, and
 * Openstax::PagePresenter stamps the chapter glossary's definition onto it as
 * `data-definition` at render time (the stored HTML is never edited). Each
 * stamped term becomes a keyboard-reachable trigger for a small dialog
 * anchored under it (above when there is < 200px below the fold): the term,
 * its definition, a link to the chapter's Key Terms page, and an Esc hint.
 * One popover at a time; Esc, outside tap, or re-tap closes it; focus moves
 * into the popover and back to the term on close. Terms without a definition
 * are left alone.
 *
 * Usage: data-controller="term-popover" on the page wrapper, with
 * data-term-popover-glossary-href-value pointing at the chapter's Key Terms
 * page (optional).
 */
const WIDTH = 320
const GAP = 10
const FLIP_BELOW = 200

export default class extends Controller {
  static values = {
    glossaryHref: { type: String, default: "" }
  }

  connect() {
    this.terms = Array.from(this.element.querySelectorAll('[data-type="term"][data-definition]'))
    if (!this.terms.length) return

    this.terms.forEach((term) => {
      term.classList.add("os-term-defined")
      term.setAttribute("tabindex", "0")
      term.setAttribute("role", "button")
      term.setAttribute("aria-expanded", "false")
      term.setAttribute("aria-label", `${term.textContent.trim()}: show definition`)
      term.addEventListener("click", (e) => {
        e.preventDefault()
        this.#toggle(term)
      })
      term.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          this.#toggle(term)
        }
      })
    })

    this.onDocClick = (e) => {
      if (!this.open) return
      if (e.target.closest(".term-popover") || e.target.closest(".os-term-defined")) return
      this.#close()
    }
    this.onKey = (e) => {
      if (e.key === "Escape" && this.open) {
        e.preventDefault()
        this.#close({ refocus: true })
      }
    }
    this.onReflow = () => this.open && this.#position()
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKey)
    window.addEventListener("resize", this.onReflow)
    window.addEventListener("scroll", this.onReflow, { passive: true })
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKey)
    window.removeEventListener("resize", this.onReflow)
    window.removeEventListener("scroll", this.onReflow)
    this.popover?.remove()
    this.popover = null
    this.open = null
  }

  #toggle(term) {
    if (this.open === term) this.#close({ refocus: true })
    else this.#show(term)
  }

  #show(term) {
    if (this.open) this.#close()
    const popover = this.#popover()
    popover.querySelector(".term-popover-term").textContent = term.textContent.trim()
    popover.querySelector(".term-popover-definition").textContent = term.dataset.definition
    popover.hidden = false
    term.setAttribute("aria-expanded", "true")
    this.open = term
    this.#position()
    popover.focus({ preventScroll: true })
  }

  #close({ refocus = false } = {}) {
    if (!this.open) return
    const term = this.open
    term.setAttribute("aria-expanded", "false")
    this.open = null
    if (this.popover) this.popover.hidden = true
    if (refocus) term.focus({ preventScroll: true })
  }

  #popover() {
    if (this.popover) return this.popover
    const el = document.createElement("div")
    el.className = "term-popover"
    el.setAttribute("role", "dialog")
    el.setAttribute("aria-label", "Definition")
    el.setAttribute("tabindex", "-1")
    el.hidden = true

    const term = document.createElement("h4")
    term.className = "term-popover-term"
    const definition = document.createElement("p")
    definition.className = "term-popover-definition"
    el.appendChild(term)
    el.appendChild(definition)

    const footer = document.createElement("div")
    footer.className = "term-popover-footer"
    if (this.glossaryHrefValue) {
      const link = document.createElement("a")
      link.className = "term-popover-link"
      link.href = this.glossaryHrefValue
      link.textContent = "Open in Key Terms →"
      footer.appendChild(link)
    } else {
      footer.appendChild(document.createElement("span"))
    }
    const esc = document.createElement("span")
    esc.className = "term-popover-esc"
    esc.textContent = "Esc to close"
    footer.appendChild(esc)
    el.appendChild(footer)

    // appended to <body> so it floats above the reader chrome; the theme
    // class on <body> keeps the tokens in scope
    document.body.appendChild(el)
    this.popover = el
    return el
  }

  #position() {
    const term = this.open
    const popover = this.popover
    if (!term || !popover) return

    const rect = term.getBoundingClientRect()
    const width = Math.min(WIDTH, document.documentElement.clientWidth - 24)
    popover.style.width = `${width}px`

    const centerX = rect.left + rect.width / 2
    let left = centerX - width / 2
    left = Math.max(12, Math.min(left, document.documentElement.clientWidth - width - 12))
    popover.style.left = `${left + window.scrollX}px`
    popover.style.setProperty("--term-popover-caret", `${Math.max(16, Math.min(centerX - left - 5, width - 26))}px`)

    const above = window.innerHeight - rect.bottom < FLIP_BELOW
    popover.classList.toggle("above", above)
    const height = popover.offsetHeight
    const top = above ? rect.top - height - GAP : rect.bottom + GAP
    popover.style.top = `${top + window.scrollY}px`
  }
}
